/**
 * Responsibility: one table of watches, with the per-row controls.
 *
 * Extracted from the page so the page can render either a single table or one
 * per retailer without duplicating the column definitions - the grouped view is
 * the same table rendered several times, not a second implementation that can
 * drift from this one.
 *
 * Built on the kit's DataTable rather than MUI X DataGrid. The watchlist is a
 * handful of rows, so pagination and column virtualisation bought nothing and
 * cost a dependency plus a second styling vocabulary to keep in line with the
 * rest of the site.
 *
 * The vertical cap is lifted on phones. DataTable defaults to a bounded
 * scroller - sensible for a long list beside other page chrome - but on a
 * narrow screen it becomes a short box with its own scrollbar nested inside
 * the page's, so the watchlist scrolled differently from every other page.
 * Horizontal scrolling is kept: the columns genuinely do not fit, and that is
 * the one axis where an inner scroller is wanted.
 */
import { sortableNumber, sortableText } from '../shelf/sortValues';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PauseIcon from '@mui/icons-material/PauseCircleOutline';
import PlayIcon from '@mui/icons-material/PlayCircleOutline';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { DataTable } from '@kit/components/data';
import type { DataColumn } from '@kit/components/data';
import { ToolbarIconButton } from '@kit/components/buttons';
import type { Watch } from '../../api/client';

type WatchTableProps = {
  rows: Watch[];
  loading?: boolean;
  busy?: boolean;
  showRetailer?: boolean;
  onToggle: (watch: Watch) => void;
  onRemove: (watch: Watch) => void;
};

const RETAILER_LABELS: Record<string, string> = {
  TARGET: 'Target',
  WALMART: 'Walmart',
  BESTBUY: 'Best Buy',
  COSTCO: 'Costco',
  AMAZON: 'Amazon',
  MOCK: 'Canary',
};

function money(value: string | number | null | undefined, fallback: string): string {
  return value ? `$${value}` : fallback;
}

/**
 * Effectively no vertical cap on phones.
 *
 * `max-height: none` is what this means, and ScrollPanel would accept that
 * string, but DataTable's own prop is typed `number | Record<string, number>`
 * and does not forward it. A number past any plausible watchlist height does
 * the same job without patching the kit, which has to stay a faithful copy of
 * the template to be re-syncable.
 */
const UNCAPPED = 100_000;

export function WatchTable({
  rows,
  loading = false,
  busy = false,
  showRetailer = true,
  onToggle,
  onRemove,
}: WatchTableProps) {
  const retailerColumn: DataColumn<Watch>[] = showRetailer
    ? [
        {
          id: 'retailer',
          label: 'Retailer',
          width: 110,
          render: (row) => RETAILER_LABELS[row.retailer] ?? row.retailer,
          sortValue: (row) => row.retailer,
        },
      ]
    : [];

  const columns: DataColumn<Watch>[] = [
    {
      id: 'product_name',
      label: 'Product',
      render: (row) => row.product_name,
      sortValue: (row) => row.product_name,
    },
    ...retailerColumn,
    {
      id: 'identifier',
      label: 'Item',
      width: 120,
      render: (row) => row.identifier,
      sortValue: (row) => sortableText(row.identifier),
    },
    {
      id: 'notify_lists',
      label: 'Notify lists',
      // By how many lists, then by their names: "which watches shout at
      // the most people" is the question this column gets opened for.
      sortValue: (row) => (row.notify_lists ?? []).length,
      // **Declared, and the chips do not wrap.** Without a width this cell
      // grew to fit every chip, and a watch on four lists turned one row
      // into a paragraph while every other row stayed a single line. A table
      // whose row height depends on its longest cell is unreadable at a
      // glance, which is the whole reason to use a table.
      width: 170,
      render: (row) => {
        // Optional on the generated schema because the server defaults it.
        const names = row.notify_lists ?? [];
        if (names.length === 0) {
          return (
            <Typography variant="caption" color="text.secondary">
              none
            </Typography>
          );
        }
        const [first, ...rest] = names;
        return (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Chip
              size="small"
              variant="outlined"
              label={first}
              sx={{ maxWidth: rest.length > 0 ? 104 : '100%' }}
            />
            {rest.length > 0 && (
              // The count rather than the names: the names are in the row's
              // own dialog, and the question this column answers from across
              // the page is "is anyone being told about this one".
              <Typography variant="caption" color="text.secondary" noWrap>
                +{rest.length}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      id: 'msrp',
      sortValue: (row) => sortableNumber(row.price_rule?.msrp_override),
      label: 'MSRP',
      align: 'right',
      width: 90,
      render: (row) => money(row.price_rule?.msrp_override, 'unset'),
    },
    {
      id: 'ceiling',
      sortValue: (row) => sortableNumber(row.price_rule?.absolute_max),
      label: 'Max',
      align: 'right',
      width: 90,
      render: (row) => money(row.price_rule?.absolute_max, 'x1.15'),
    },
    {
      id: 'interval',
      label: 'Every',
      align: 'right',
      width: 80,
      render: (row) => `${row.baseline_interval_seconds}s`,
      sortValue: (row) => row.baseline_interval_seconds,
    },
    {
      id: 'enabled',
      label: 'State',
      width: 90,
      render: (row) => (
        <Typography variant="body2" sx={{ color: row.enabled ? 'primary.main' : 'text.secondary' }}>
          {row.enabled ? 'polling' : 'paused'}
        </Typography>
      ),
      sortValue: (row) => (row.enabled ? 0 : 1),
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
      width: 96,
      render: (row) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <ToolbarIconButton
            aria-label={row.enabled ? 'Pause polling' : 'Resume polling'}
            disabled={busy}
            onClick={() => onToggle(row)}
          >
            {row.enabled ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
          </ToolbarIconButton>
          <ToolbarIconButton
            aria-label="Stop tracking"
            onClick={() => onRemove(row)}
          >
            <DeleteIcon fontSize="small" />
          </ToolbarIconButton>
        </Stack>
      ),
    },
  ];

  return (
    <DataTable
      rows={rows}
      columns={columns}
      rowKey={(row, index) => row.id ?? `row-${index}`}
      loading={loading}
      emptyMessage="Nothing tracked here yet."
      defaultSortBy="product_name"
      // Uncapped on xs so the table grows and the page scrolls as one; the
      // desktop cap is left alone, per the kit's responsive contract.
      maxHeight={{ xs: UNCAPPED, md: 540 }}
    />
  );
}
